import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Form,
  Button,
  Alert,
  ListGroup,
  InputGroup,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { useCurrentAccount } from "@mysten/dapp-kit";
import { SuiClient } from "@mysten/sui/client";
import Long from "long"; // Added for timestamp handling
import { decryptMessage } from "./utils/encryption";
import { retrieveFromWalrus } from "./utils/walrus";
import { retrieveFromIPFS } from "./utils/ipfs";
import logger from './utils/logger';
import { RiChatNewLine, RiMessage2Line, RiTeamLine, RiLoader4Line, RiPhoneLine } from 'react-icons/ri';
import { useCall } from './context/CallContext';

// Simple key derivation from addresses (for testing - in production use proper key exchange)
function deriveSharedKeyFromAddresses(address1, address2) {
  // Sort addresses to ensure consistent key derivation regardless of order
  const [addr1, addr2] = [address1, address2].sort();

  // Create a deterministic key from the address pair
  const combined = addr1 + addr2 + 'su-messaging-salt';

  // Use Web Crypto API to hash and get raw bytes
  const encoder = new TextEncoder();
  const data = encoder.encode(combined);

  return crypto.subtle.digest('SHA-256', data).then(hash => {
    return new Uint8Array(hash);
  });
}

// Helper function to decrypt a message from storage (same as in Chat.js)
const decryptMessageFromStorage = async (metadataString, senderAddress, recipientAddress) => {
  try {
    const metadata = JSON.parse(metadataString);

    if (metadata.version === "sui-compressed-v1" && metadata.compressedData) {
      // Ultimate fallback: compressed data stored on Sui
      logger.log("Decrypting from Sui compressed fallback storage");
      const encryptedData = atob(metadata.compressedData); // Decompress
      const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
      const decryptedContent = await decryptMessage(encryptedData, sharedKey);
      return decryptedContent;
    } else if (metadata.version === "ipfs-fallback-v1" && metadata.cid) {
      // IPFS decentralized fallback
      logger.log("Decrypting from IPFS decentralized fallback, CID:", metadata.cid);
      const encryptedData = await retrieveFromIPFS(metadata.cid);
      const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
      const decryptedContent = await decryptMessage(encryptedData, sharedKey);
      return decryptedContent;
    } else if (metadata.version === "sui-fallback-v1" && metadata.encryptedData) {
      // Legacy fallback: encrypted data stored directly on Sui
      logger.log("Decrypting from Sui fallback storage");
      const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
      const decryptedContent = await decryptMessage(metadata.encryptedData, sharedKey);
      return decryptedContent;
    } else if (metadata.version === "walrus-v1" && metadata.blobId) {
      // Normal Walrus storage
      logger.log("Decrypting from Walrus storage, blobId:", metadata.blobId);
      const encryptedData = await retrieveFromWalrus(metadata.blobId);
      const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
      const decryptedContent = await decryptMessage(encryptedData, sharedKey);
      return decryptedContent;
    } else {
      // Fallback for old format (plain text)
      return metadataString;
    }
  } catch (error) {
    logger.error('Failed to decrypt message from storage:', error);
    // Return a placeholder indicating decryption failed
    return "[Message temporarily unavailable]";
  }
};

function Dashboard() {
  const [recipientAddress, setRecipientAddress] = useState("");
  const [recentChats, setRecentChats] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuColor, setMenuColor] = useState("#ff00ff"); // Sync with App.js default
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [mobileView, setMobileView] = useState("chats"); // "start", "chats", or "people" for mobile
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;
  const navigate = useNavigate();

  // Reactive window resize listener
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { startCall } = useCall();
  const currentAccount = useCurrentAccount();
  const isConnected = !!currentAccount;
  const client = useMemo(
    () => new SuiClient({ url: "https://fullnode.mainnet.sui.io:443" }),
    []
  );

  const fetchRecentChats = useCallback(async () => {
    if (!isConnected || !currentAccount) return;

    setIsLoadingChats(true);
    try {
      const senderAddress = currentAccount.address;
      const packageId =
        "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045";

      let allEvents = [];

      // Single fetch with sufficient limit to avoid excessive looping
      const response = await client.queryEvents({
        query: { MoveEventType: `${packageId}::su_messaging::MessageCreated` },
        limit: 200, // Increased limit to capture more recent conversations
        order: "descending", // Most recent events first
      });
      allEvents = [...allEvents, ...response.data];

      const recipients = new Set();
      for (const event of allEvents) {
        const { sender, recipient } = event.parsedJson || {};
        if (!sender || !recipient) continue;
        if (
          (sender === senderAddress && recipient !== senderAddress) ||
          (recipient === senderAddress && sender !== senderAddress)
        ) {
          if (sender === senderAddress) recipients.add(recipient);
          else recipients.add(sender);
        }
      }

      const recipientList = Array.from(recipients);
      const chatData = await Promise.all(
        recipientList.map(async (address) => {
          const objects = await client.getOwnedObjects({
            owner: address,
            options: { showType: true, showContent: true },
          });
          const userObject = objects.data.find((obj) =>
            obj.data.type.includes(
              "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045::su_messaging::User"
            )
          );
          const displayName = userObject?.data.content.fields.display_name
            ? new TextDecoder().decode(
                new Uint8Array(userObject.data.content.fields.display_name)
              )
            : address.slice(0, 6) + "...";

          let lastMessage = "No messages yet";
          let lastMessageTime = 0;

          // Find the most recent message in this conversation from the events
          for (const event of allEvents) {
            const eventData = event.parsedJson || {};
            const { sender, recipient, message_id, timestamp } = eventData;
            
            // Check if this event is part of our conversation
            const isOurConversation = 
              (sender === senderAddress && recipient === address) ||
              (sender === address && recipient === senderAddress);
            
            if (isOurConversation && timestamp && message_id) {
              const messageTime = Long.fromValue(timestamp).toNumber();
              if (messageTime > lastMessageTime) {
                try {
                  // Fetch the message object to get the content
                  const messageObject = await client.getObject({
                    id: message_id,
                    options: { showContent: true },
                  });
                  
                  if (messageObject.data?.content?.fields?.encrypted_content) {
                    const encryptedMetadata = new TextDecoder().decode(
                      new Uint8Array(messageObject.data.content.fields.encrypted_content)
                    );

                    // Decrypt the message content
                    const sender = messageObject.data.content.fields.sender;
                    const recipient = messageObject.data.content.fields.recipient;
                    lastMessage = await decryptMessageFromStorage(encryptedMetadata, sender, recipient);
                  } else {
                    lastMessage = "No content available";
                  }
                } catch (error) {
                  logger.error('Failed to fetch message content:', error);
                  lastMessage = "Error loading message";
                }
                lastMessageTime = messageTime;
              }
            }
          }

          return {
            address,
            displayName,
            lastMessage,
          };
        })
      );

      setRecentChats(chatData);
    } catch (err) {
      // Fallback: scan localStorage for conversation_ markers
      const senderAddress = currentAccount.address;
      const localRecipients = new Set();
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(`conversation_${senderAddress}_`)) {
          localRecipients.add(key.replace(`conversation_${senderAddress}_`, ''));
        }
        if (key?.startsWith(`messages_${senderAddress}_`)) {
          localRecipients.add(key.replace(`messages_${senderAddress}_`, ''));
        }
      }
      // Build minimal chatData from local keys if found
      if (localRecipients.size > 0) {
        const chatData = Array.from(localRecipients).map(address => ({
          address,
          displayName: address.slice(0, 6) + '...' + address.slice(-4),
          lastMessage: 'Local cache',
        }));
        setRecentChats(chatData);
        logger.warn('Using localStorage fallback for recent chats');
      } else {
        setError("Failed to fetch recent chats: " + err.message);
        logger.error(err);
      }
    } finally {
      setIsLoadingChats(false);
    }
  }, [isConnected, currentAccount, client]);

  useEffect(() => {
    fetchRecentChats();

    // Periodic refresh of recent chats every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchRecentChats();
    }, 30000);

    let r = 255;
    let g = 0;
    let b = 255;
    const colorInterval = setInterval(() => {
      r = (r + 1) % 256;
      g = (g + 2) % 256;
      b = (b + 3) % 256;
      setMenuColor(`rgb(${r}, ${g}, ${b})`);
    }, 100);
    return () => { clearInterval(refreshInterval); clearInterval(colorInterval); };
  }, [fetchRecentChats]);

  const handleStartChat = (e) => {
    e.preventDefault();
    setError(null);

    if (!recipientAddress.match(/^0x[a-fA-F0-9]{64}$/)) {
      setError(
        "Please enter a valid SUI address (64 characters starting with 0x)."
      );
      return;
    }

    navigate(`/chat/${recipientAddress}`, {
      state: { clearNewMessages: true },
    });
  };

  const filteredChats = recentChats.filter(
    (chat) =>
      chat.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chat.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container
      className="mt-3"
      style={{
        maxWidth: "1200px", // Back to original width
        maxHeight: "80vh", // Back to original height
        height: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "8px", // Back to original gap
        background: "linear-gradient(135deg, #050010, #150030)",
        border: `3px solid ${menuColor}`, // Back to original border
        borderRadius: "10px", // Back to original radius
        boxShadow: "0 0 18px rgba(0, 255, 255, 0.6)", // Back to original shadow
        fontFamily: "Orbitron, sans-serif",
        color: "#00ffff",
        padding: "10px", // Back to original padding
        overflow: "auto",
      }}
    >
      <h2
        className="text-center"
        style={{
          textShadow: "0 0 15px #00ffff", // Smaller shadow
          marginBottom: "8px", // Reduced margin
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px", // Reduced gap
          fontSize: "20px", // Smaller font
        }}
      >
        Dashboard
      </h2>
      <div
        style={{
          display: "flex",
          gap: "10px",
          justifyContent: "space-between",
          minHeight: "400px",
          flexDirection: isMobile ? "column" : "row", // Stack on mobile
        }}
      >
        {/* Mobile Toggle Buttons */}
        {isMobile && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "12px",
              justifyContent: "center",
            }}
          >
            <Button
              onClick={() => setMobileView("start")}
              style={{
                backgroundColor: mobileView === "start" ? menuColor : "#0d001f",
                border: `2px solid ${menuColor}`,
                color: "#00ffff",
                textShadow: "0 0 6px #00ffff",
                padding: "8px 16px",
                borderRadius: "8px",
                fontSize: "14px",
                flex: "1",
                minHeight: "44px", // Touch-friendly
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <RiChatNewLine size={16} /> Start Chat
            </Button>
            <Button
              onClick={() => setMobileView("chats")}
              style={{
                backgroundColor: mobileView === "chats" ? menuColor : "#0d001f",
                border: `2px solid ${menuColor}`,
                color: "#00ffff",
                textShadow: "0 0 6px #00ffff",
                padding: "8px 16px",
                borderRadius: "8px",
                fontSize: "14px",
                flex: "1",
                minHeight: "44px", // Touch-friendly
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <RiMessage2Line size={16} /> Recent Chats
            </Button>
            <Button
              onClick={() => setMobileView("people")}
              style={{
                backgroundColor: mobileView === "people" ? menuColor : "#0d001f",
                border: `2px solid ${menuColor}`,
                color: "#00ffff",
                textShadow: "0 0 6px #00ffff",
                padding: "8px 16px",
                borderRadius: "8px",
                fontSize: "14px",
                flex: "1",
                minHeight: "44px", // Touch-friendly
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <RiTeamLine size={16} /> People
            </Button>
          </div>
        )}

        {/* Start Chat Form - Show on desktop or when selected on mobile */}
        {(window.innerWidth >= 768 || mobileView === "start") && (
          <Form
            onSubmit={handleStartChat}
            style={{
              flex: isMobile ? "none" : "1 1 300px",
              width: isMobile ? "100%" : "auto",
              minWidth: isMobile ? "auto" : "250px",
              border: `2px solid ${menuColor}`,
              padding: isMobile ? "16px" : "8px",
              borderRadius: isMobile ? "8px" : "6px",
              background: "linear-gradient(135deg, #050010, #0d001f)",
              boxShadow: isMobile ? "0 0 15px rgba(0, 255, 255, 0.4)" : "0 0 12px rgba(0, 255, 255, 0.4)",
              marginBottom: isMobile ? "16px" : "0",
            }}
          >
          <Form.Group controlId="recipientAddress">
            <Form.Label style={{ textShadow: "0 0 6px #ff00ff" }}>
              Start a Chat
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter recipient's SUI address (e.g., 0x...)"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              required
              style={{
                backgroundColor: "white",
                color: "black",
                border: `1px dashed ${menuColor}`,
                borderRadius: "4px", // Smaller radius
                padding: "6px", // Reduced padding
                fontSize: "12px", // Smaller font
                textShadow: "0 0 2px #ccffcc", // Smaller shadow
                transition: "border-color 0.4s",
              }}
            />
          </Form.Group>
          <Button
            variant="primary"
            type="submit"
            className="mt-2"
            style={{
              backgroundColor: menuColor,
              borderColor: menuColor,
              textShadow: "0 0 5px #00ffff", // Smaller shadow
              padding: "6px 12px", // Reduced padding
              fontSize: "12px", // Smaller font
              transition: "background-color 0.4s",
              width: "100%",
              borderRadius: "4px", // Smaller radius
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#00ffff")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = menuColor)}
          >
            Start Chat
          </Button>
        </Form>
        )}

        {/* Recent Chats Section - Show on desktop or when selected on mobile */}
        {(window.innerWidth >= 768 || mobileView === "chats") && (
          <div
            style={{
              flex: isMobile ? "none" : "2 1 400px",
              width: isMobile ? "100%" : "auto",
              minWidth: isMobile ? "auto" : "300px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginBottom: isMobile ? "16px" : "0",
            }}
          >
          <InputGroup
            style={{
              height: "32px", // Reduced height
              marginBottom: "12px", // Space between search and title
            }}
          >
            <Form.Control
              type="text"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                backgroundColor: "white",
                color: "black",
                border: `1px dashed ${menuColor}`,
                borderRadius: "4px", // Smaller radius
                padding: "6px", // Reduced padding
                fontSize: "12px", // Smaller font
                textShadow: "0 0 2px #ccffcc", // Smaller shadow
                transition: "border-color 0.4s",
                height: "32px", // Reduced height
              }}
            />
          </InputGroup>
          <h4 style={{ textShadow: "0 0 10px #00ffff", marginBottom: "8px", fontSize: "14px" }}>
            Recent Chats
          </h4>
          <div
            style={{
              maxHeight: "320px",
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarWidth: "thin",
              scrollbarColor: "#ff00ff transparent",
            }}
          >
            {isLoadingChats ? (
              <div
                style={{
                  background: "rgba(0, 0, 0, 0.5)",
                  border: `2px solid ${menuColor}`,
                  borderRadius: "4px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 0 12px rgba(0, 255, 255, 0.4)",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    color: "#00ffff",
                    textShadow: "0 0 8px #00ffff",
                    marginBottom: "15px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <RiLoader4Line size={20} style={{ animation: "spin 1s linear infinite" }} /> ITERATING THROUGH THE BLOCKCHAIN
                </div>
                <style>{`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
                <div
                  style={{
                    marginTop: "15px",
                    fontSize: "12px",
                    color: "#00ffff",
                    opacity: 0.8,
                  }}
                >
                  Scanning blockchain for your messages...
                </div>
              </div>
            ) : filteredChats.length === 0 ? (
              <p style={{ color: "#00ffff" }}>No recent chats yet.</p>
            ) : (
              <ListGroup
                style={{
                  background: "rgba(0, 0, 0, 0.5)",
                  border: `2px solid ${menuColor}`,
                  borderRadius: "4px", // Smaller radius
                  boxShadow: "0 0 12px rgba(0, 255, 255, 0.4)", // Smaller shadow
                }}
              >
                {filteredChats.map((chat, index) => (
                  <ListGroup.Item
                    key={index}
                    style={{
                      backgroundColor: "#050010",
                      color: "#00ffff",
                      position: "relative",
                      padding: "6px", // Reduced padding
                      display: "flex",
                      flexDirection: "column",
                      borderBottom: `1px dashed ${menuColor}`,
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#00ccff")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#050010")
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px", // Reduced gap
                      }}
                    >
                      <span
                        style={{
                          width: "8px", // Smaller indicator
                          height: "8px", // Smaller indicator
                          backgroundColor: "#00ff00", // Always green for active chats
                          borderRadius: "50%",
                          border: `1px solid ${menuColor}`,
                        }}
                      />
                      <Link
                        to={`/chat/${chat.address}`}
                        style={{
                          color: "#00ffff",
                          textDecoration: "none",
                          fontWeight: "bold",
                          marginBottom: "3px", // Reduced margin
                          textShadow: "0 0 4px #ff00ff", // Smaller shadow
                          flex: "1",
                          fontSize: "12px", // Smaller font
                        }}
                      >
                        {chat.displayName}
                      </Link>
                    </div>
                    <div
                      style={{
                        color: "#00ffff",
                        fontWeight: "normal",
                        padding: "8px", // Larger padding
                        backgroundColor: "#0d001f",
                        borderRadius: "6px", // Larger radius
                        maxWidth: "85%",
                        wordWrap: "break-word",
                        border: `1px dashed ${menuColor}`,
                        boxShadow: "0 0 10px rgba(0, 255, 255, 0.4)",
                        fontSize: "14px", // Larger font for mobile
                        marginTop: "8px",
                      }}
                    >
                      {chat.lastMessage || "No message content available"}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </div>
      )}

      {/* People Section - Show on desktop or when selected on mobile */}
      {(window.innerWidth >= 768 || mobileView === "people") && (
        <div
          style={{
            flex: isMobile ? "none" : "2 1 400px",
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? "auto" : "300px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: isMobile ? "16px" : "0",
          }}
        >
          <div
            style={{
              overflow: "auto",
            }}
          >
            <h4 style={{ textShadow: "0 0 10px #00ffff", marginBottom: "4px", fontSize: "14px" }}>
              Contacts
            </h4>
            {recentChats.length === 0 ? (
              <p style={{ color: "#00ffff" }}>No contacts yet. Start a chat to add contacts!</p>
            ) : (
              <ListGroup
                style={{
                  background: "rgba(0, 0, 0, 0.5)",
                  border: `2px solid ${menuColor}`,
                  borderRadius: "4px",
                  boxShadow: "0 0 12px rgba(0, 255, 255, 0.4)",
                }}
              >
                {recentChats.map((chat, index) => (
                  <ListGroup.Item
                    key={index}
                    style={{
                      backgroundColor: "#050010",
                      color: "#00ffff",
                      position: "relative",
                      padding: "6px",
                      display: "flex",
                      flexDirection: "column",
                      borderBottom: `1px dashed ${menuColor}`,
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#00ccff")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#050010")
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          backgroundColor: "#00ff00",
                          borderRadius: "50%",
                          border: `1px solid ${menuColor}`,
                        }}
                      />
                      <Link
                        to={`/chat/${chat.address}`}
                        style={{
                          color: "#00ffff",
                          textDecoration: "none",
                          fontWeight: "bold",
                          marginBottom: "3px",
                          textShadow: "0 0 4px #ff00ff",
                          flex: "1",
                          fontSize: "12px",
                        }}
                      >
                        {chat.displayName}
                      </Link>
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          startCall(chat.address, chat.displayName);
                        }}
                        style={{
                          backgroundColor: "transparent",
                          border: `1px solid ${menuColor}`,
                          color: menuColor,
                          fontSize: "12px",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = menuColor;
                          e.target.style.color = '#050010';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = menuColor;
                        }}
                      >
                        <RiPhoneLine size={12} /> Call
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </div>
      )}


      </div>
      {error && (
        <Alert
          variant="danger"
          className="mt-2"
          style={{
            backgroundColor: "#0d001f",
            border: `1px solid ${menuColor}`,
            color: "#ff0000",
            position: "absolute",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "90%",
            maxWidth: "600px",
            boxShadow: "0 0 15px rgba(255, 0, 0, 0.5)",
          }}
        >
          {error}
        </Alert>
      )}
    </Container>
  );
}

export default Dashboard;
